import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { sectionErrorText, SWATCH } from '../../shared/section-form'
import {
  addSwatch,
  fromPickerValue,
  toPickerValue,
} from '../../shared/swatch-list'
import type { BoSection } from '../../shared/types'

export interface AddColourDialogProps {
  open: boolean
  section?: BoSection
  saving?: boolean
  error?: string
  onClose: () => void
  onAdd: (swatches: string[]) => void
}

export default function AddColourDialog({
  open,
  section,
  saving = false,
  error,
  onClose,
  onAdd,
}: AddColourDialogProps) {
  const { formatMessage } = useIntl()
  const [draft, setDraft] = useState('')
  const [refused, setRefused] = useState<'invalid' | 'duplicate' | undefined>(
    undefined,
  )

  useEffect(() => {
    if (open) {
      setDraft('')
      setRefused(undefined)
    }
  }, [open])

  if (!section) {
    return null
  }

  const existing = section.swatches ?? []
  const valid = SWATCH.test(draft)

  const submit = () => {
    const result = addSwatch(existing, draft)
    if (!result.ok) {
      setRefused(result.reason)

      return
    }
    onAdd(result.swatches)
  }

  const setFrom = (raw: string) => {
    setRefused(undefined)
    setDraft(raw.replace(/^#/, '').toUpperCase())
  }

  const helper = () => {
    if (refused === 'duplicate') {
      return formatMessage(
        { id: 'avatarStudio.editor.swatches.duplicate' },
        { hex: draft.toUpperCase() },
      )
    }
    if (refused === 'invalid' || (draft !== '' && !valid)) {
      return formatMessage({ id: 'avatarStudio.editor.swatches.format' })
    }

    return undefined
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth={true} maxWidth="xs">
      <DialogTitle>
        {formatMessage(
          { id: 'avatarStudio.addColour.title' },
          { section: section.displayName ?? section.id },
        )}
      </DialogTitle>
      <DialogContent dividers={true}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <Box
              component="input"
              type="color"
              aria-label={formatMessage({ id: 'avatarStudio.editor.swatches.pick' })}
              value={toPickerValue(draft)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setFrom(fromPickerValue(e.target.value))
              }
              data-testid="add-colour-picker"
              sx={{
                width: 56,
                height: 56,
                p: 0.25,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'background.paper',
                cursor: 'pointer',
              }}
            />
            <TextField
              size="small"
              autoFocus={true}
              label={formatMessage({ id: 'avatarStudio.editor.swatches.add' })}
              value={draft}
              onChange={(e) => setFrom(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  submit()
                }
              }}
              error={Boolean(refused) || (draft !== '' && !valid)}
              helperText={helper()}
              FormHelperTextProps={{ role: 'alert' }}
              inputProps={{ maxLength: 7, spellCheck: false }}
              sx={{ flexGrow: 1 }}
            />
          </Stack>

          <Typography variant="caption" color="text.secondary">
            {formatMessage(
              { id: 'avatarStudio.addColour.position' },
              { count: existing.length + 1 },
            )}
          </Typography>

          {error && (
            <Alert severity="error">
              {sectionErrorText(error, (id) => formatMessage({ id }))}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {formatMessage({ id: 'avatarStudio.editor.cancel' })}
        </Button>
        <Button variant="contained" onClick={submit} disabled={saving || !valid}>
          {formatMessage({
            id: saving
              ? 'avatarStudio.editor.saving'
              : 'avatarStudio.grid.addColour',
          })}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
